import {
  ILayout,
  IModal,
  IPage,
  IVariationContainer,
} from 'marketing-site/@types/generated/contentful'
import buildPath from 'marketing-site/lib/buildPath'
import PageBehaviors from 'marketing-site/lib/page-behaviors/PageBehaviors'
import { renderBodyContent } from 'marketing-site/lib/render'
import FaviconLinks from 'marketing-site/src/components/common/FaviconLinks'
import LocaleLinks from 'marketing-site/src/components/common/LocaleLinks'
import SEOMetadata, { ISeoProps } from 'marketing-site/src/components/common/SEOMetadata'
import CurrentPathContext from 'marketing-site/src/components/context/CurrentPathContext'
import EditingContext from 'marketing-site/src/components/context/EditingContext'
import PageContext from 'marketing-site/src/components/context/PageContext'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import { BrandOptions, BrandProvider } from 'marketing-site/src/library/providers/brandProvider'
import { SpacingProvider } from 'marketing-site/src/library/providers/spacingProvider'
import { ToggleProvider } from 'marketing-site/src/library/providers/toggleProvider'
import { ContentfulLayout } from 'marketing-site/src/transformers/components/ContentfulLayout'
import { isVariationContainer } from 'marketing-site/src/transformers/components/ContentfulVariationContainer'
import { transformSeoMetadata } from 'marketing-site/src/transformers/SEOMetadata'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import { useAssignedVariations } from '../context/AssignedVariationsContext'
import { ChatWithUsProvider } from '../context/ChatWithUsContext'
import { IntersectionProvider } from '../context/IntersectionContext'
import { MediaProvider } from '../context/MediaContext'
import { ModalProvider } from '../context/ModalContext'
import GtmTrackingInitializer from './GtmTrackingInitializer'

interface IProps {
  page: IPage
  seo?: ISeoProps
  title?: string
  children?: React.ReactNode
}

const getLayoutFromVariation = (layoutFields: IVariationContainer, variations: any): ILayout => {
  const experimentKey = layoutFields?.fields?.experimentKey
  if (experimentKey && !variations[experimentKey]) {
    return layoutFields?.fields?.variations?.shift() as unknown as ILayout
  }
  const variationKey =
    (experimentKey && variations[experimentKey] && variations[experimentKey].variationKey) ||
    'control'
  const layoutKey =
    variationKey && layoutFields?.fields?.meta && layoutFields?.fields?.meta[variationKey]
  const layout = layoutFields?.fields?.variations?.filter((it: any) => it.sys.id === layoutKey)[0]

  return layout as unknown as ILayout
}
export function Page({ page, children, seo, title }: IProps) {
  const { fields } = page
  const localeCode = useLocaleCode()
  const variations = useAssignedVariations()
  const router = useRouter()
  const { useRefreshBrand, useSpacing } = page.fields

  const showEditing = process.env.EDITOR_UI === 'true' && !!router.query.editing
  const layout =
    fields.layout && isVariationContainer(fields.layout)
      ? getLayoutFromVariation(fields.layout as IVariationContainer, variations)
      : fields.layout
  const modalFields = layout?.fields.modal
  const modalInitialOpen = (modalFields as IModal)?.fields.modalId
  const secondaryNavFields = layout?.fields.secondaryNavigation?.fields
  const secondaryNavItems =
    secondaryNavFields?.menuDirection === 'horizontal'
      ? secondaryNavFields.sections.map((section) => section.fields.url.split('#')[1])
      : []

  const initialSecondaryNavItem = Array.isArray(secondaryNavItems) ? secondaryNavItems[0] : ''

  const brand = useRefreshBrand ? BrandOptions.default : BrandOptions.legacy

  const renderPage = () => (
    <PageContext.Provider value={page}>
      <CurrentPathContext.Provider
        value={buildPath({ localeCode, pathname: fields.path, preserveQuery: true })}
      >
        <EditingContext.Provider value={showEditing}>
          <ChatWithUsProvider>
            <GtmTrackingInitializer />
            <MediaProvider>
              <IntersectionProvider
                navTitle={secondaryNavFields?.title}
                initialItem={initialSecondaryNavItem}
              >
                <ModalProvider initialOpen={modalInitialOpen}>
                  <PageBehaviors pageBehaviors={fields.behaviors || []}>
                    <ContentfulLayout {...layout}>
                      <Head>
                        <title>{title || fields.title}</title>
                        <meta name="viewport" content="initial-scale=1, width=device-width" />
                      </Head>
                      <FaviconLinks />
                      <LocaleLinks />
                      <SEOMetadata {...transformSeoMetadata(fields.seoMetadata)} {...seo} />
                      {children ? children : renderBodyContent(fields.bodyContent, brand)}
                    </ContentfulLayout>
                  </PageBehaviors>
                </ModalProvider>
              </IntersectionProvider>
            </MediaProvider>
          </ChatWithUsProvider>
        </EditingContext.Provider>
      </CurrentPathContext.Provider>
    </PageContext.Provider>
  )

  if (typeof useRefreshBrand !== 'boolean') {
    return renderPage()
  }

  return (
    <BrandProvider forcedBrand={brand}>
      <SpacingProvider useSpacing={brand === BrandOptions.default && useSpacing}>
        <ToggleProvider brand={brand}>{renderPage()}</ToggleProvider>
      </SpacingProvider>
    </BrandProvider>
  )
}

export default Page
